import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["form", "messages", "bodyRich", "submit", "loading"];
  static values = { messagesUrl: String };

  connect() {
    this.loadMessages();
    this.scrollToBottom = this.scrollToBottom.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.element.addEventListener("trix-focus", this.scrollToBottom);
    this.element.addEventListener("trix-blur", this.scrollToBottom);
    this.element.addEventListener("focusin", this.handleFocusIn);
    this.element.addEventListener("focusout", this.handleFocusOut);
  }

  disconnect() {
    this.element.removeEventListener("trix-focus", this.scrollToBottom);
    this.element.removeEventListener("trix-blur", this.scrollToBottom);
    this.element.removeEventListener("focusin", this.handleFocusIn);
    this.element.removeEventListener("focusout", this.handleFocusOut);
  }

  handleFocusIn(event) {
    if (event.target.tagName === "TRIX-EDITOR" || event.target.matches("textarea, input[type='text']")) {
      this.scrollToBottom();
    }
  }

  handleFocusOut(event) {
    if (event.target.tagName === "TRIX-EDITOR" || event.target.matches("textarea, input[type='text']")) {
      this.scrollToBottom();
    }
  }

  scrollToBottom() {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
  }

  loadMessages() {
    if (!this.hasMessagesUrlValue) return;

    fetch(this.messagesUrlValue, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    })
      .then((response) => response.json())
      .then((data) => {
        if (data && data.html != null) {
          this.messagesTarget.innerHTML = data.html;
        }
      })
      .catch(() => {
        if (this.hasLoadingTarget) {
          this.loadingTarget.innerHTML = '<span class="text-danger">Failed to load messages.</span>';
        }
      });
  }

  submit(event) {
    event.preventDefault();

    if (this.isEmpty()) {
      window.notyf && window.notyf.error("Please enter a message before sending.");
      return;
    }

    const formData = new FormData(this.formTarget);
    const url = this.formTarget.getAttribute("action");

    this.setSubmitting(true);

    fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": localStorage.getItem("bp-x-csrf-token"),
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
      body: formData,
    })
      .then((response) => {
        const isJson = (response.headers.get("content-type") || "").includes("application/json");
        return response.json().catch(() => ({})).then((data) => ({ ok: response.ok, data, isJson }));
      })
      .then(({ ok, data }) => {
        if (ok && data.html) {
          this.appendMessage(data.html);
          this.resetEditor();
        } else {
          const message = (data && data.error) || "Unable to send reply.";
          window.notyf && window.notyf.error(message);
        }
      })
      .catch(() => {
        window.notyf && window.notyf.error("Something went wrong. Please try again.");
      })
      .finally(() => {
        this.setSubmitting(false);
      });
  }

  isEmpty() {
    if (this.hasBodyRichTarget) {
      const trixInput = this.bodyRichTarget;
      const trixEditor = this.formTarget.querySelector(`trix-editor[input="${trixInput.id}"]`);
      const value = trixEditor ? trixEditor.innerText.trim() : (trixInput.value || "").trim();
      return value === "";
    }
    return false;
  }

  appendMessage(html) {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html.trim();
    Array.from(wrapper.children).forEach((node) => {
      this.messagesTarget.appendChild(node);
    });

    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "instant" });
  }

  resetEditor() {
    if (!this.hasBodyRichTarget) return;
    const trixInput = this.bodyRichTarget;
    const trixEditor = this.formTarget.querySelector(`trix-editor[input="${trixInput.id}"]`);
    if (trixEditor && trixEditor.editor) {
      trixEditor.editor.loadHTML("");
    } else {
      trixInput.value = "";
    }
  }

  setSubmitting(isSubmitting) {
    if (!this.hasSubmitTarget) return;
    const btn = this.submitTarget;
    if (isSubmitting) {
      btn.dataset.originalHtml = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i> Sending...';
    } else {
      btn.disabled = false;
      if (btn.dataset.originalHtml) {
        btn.innerHTML = btn.dataset.originalHtml;
        delete btn.dataset.originalHtml;
      }
    }
  }
}
